import { Marked } from 'marked';
import { DOC_SECTIONS } from './docs-catalog.js';

function slugify(text) {
  return String(text)
    .toLowerCase()
    .replace(/<[^>]+>/g, '')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
}

function parse(markdown) {
  const slugCounts = new Map();

  const marked = new Marked({
    gfm: true,
    breaks: false,
    renderer: {
      heading({ tokens, depth }) {
        const text = this.parser.parseInline(tokens);
        const base = slugify(text);
        const count = slugCounts.get(base) || 0;
        slugCounts.set(base, count + 1);
        const id = count ? `${base}-${count}` : base;
        return `<h${depth} id="${id}">${text}</h${depth}>\n`;
      },
      code({ text, lang }) {
        const language = lang ? ` class="language-${lang}"` : '';
        const escaped = text
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
        return `<pre class="code"><code${language}>${escaped}</code></pre>\n`;
      },
      table(token) {
        const header = token.header
          .map((cell) => `<th>${this.parser.parseInline(cell.tokens)}</th>`)
          .join('');
        const body = token.rows
          .map(
            (row) =>
              `<tr>${row
                .map((cell) => `<td>${this.parser.parseInline(cell.tokens)}</td>`)
                .join('')}</tr>`
          )
          .join('');
        return `<div class="doc-table-wrap"><table class="doc-table"><thead><tr>${header}</tr></thead><tbody>${body}</tbody></table></div>\n`;
      },
      link({ href, title, tokens }) {
        const text = this.parser.parseInline(tokens);
        const isExternal = /^https?:\/\//i.test(href);
        const isContact = href.includes('contact.html');
        const attrs = isExternal
          ? ' rel="noopener noreferrer" target="_blank"'
          : isContact
            ? ''
            : ' data-doc-link';
        const titleAttr = title ? ` title="${title}"` : '';
        return `<a href="${href}"${titleAttr}${attrs}>${text}</a>`;
      },
    },
  });

  return marked.parse(markdown);
}

function renderSections(root) {
  const main = root.querySelector('[data-docs-main]');
  if (!main) return;

  main.innerHTML = DOC_SECTIONS
    .map(
      (section) => `
    <article class="doc-section" id="${section.id}" data-doc-section="${section.id}">
      <div class="doc-prose">${parse(section.markdown)}</div>
    </article>`
    )
    .join('');
}

function buildNav(root) {
  const nav = root.querySelector('[data-docs-nav]');
  if (!nav) return;

  nav.innerHTML = DOC_SECTIONS
    .map(
      (section) =>
        `<a href="#${section.id}" data-doc-nav="${section.id}">${section.title}</a>`
    )
    .join('');
}

function scrollToDocTarget(hash) {
  const id = hash.replace(/^#/, '');
  if (!id) return false;

  const section = DOC_SECTIONS.find((s) => s.id === id);
  const target = document.getElementById(id);
  if (!target) return false;

  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  history.replaceState(null, '', `#${id}`);

  if (section) {
    document.querySelectorAll('[data-doc-nav]').forEach((link) => {
      link.classList.toggle('is-active', link.getAttribute('data-doc-nav') === id);
    });
  }

  return true;
}

function bindNav(root) {
  const links = [...root.querySelectorAll('[data-doc-nav]')];
  const sections = [...root.querySelectorAll('[data-doc-section]')];
  const main = root.querySelector('[data-docs-main]');

  const setActive = (id) => {
    links.forEach((link) => {
      link.classList.toggle('is-active', link.getAttribute('data-doc-nav') === id);
    });
  };

  links.forEach((link) => {
    link.addEventListener('click', (event) => {
      event.preventDefault();
      const id = link.getAttribute('data-doc-nav');
      scrollToDocTarget(`#${id}`);
      setActive(id);
    });
  });

  main?.querySelectorAll('a[data-doc-link]').forEach((anchor) => {
    anchor.addEventListener('click', (event) => {
      const href = anchor.getAttribute('href');
      if (!href?.startsWith('#')) return;
      const id = href.slice(1);
      const target = document.getElementById(id);
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      const sectionEl = target.closest('[data-doc-section]');
      const sectionId = sectionEl?.getAttribute('data-doc-section');
      if (sectionId) {
        history.replaceState(null, '', `#${sectionId}`);
        setActive(sectionId);
      } else {
        history.replaceState(null, '', href);
      }
    });
  });

  if (sections.length && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) {
          setActive(visible.target.getAttribute('data-doc-section'));
        }
      },
      { rootMargin: '-18% 0px -58% 0px', threshold: [0, 0.2, 0.45] }
    );
    sections.forEach((section) => observer.observe(section));
  }

  const initial = window.location.hash.replace('#', '');
  if (initial && scrollToDocTarget(`#${initial}`)) {
    return;
  }
  if (links[0]) {
    setActive(links[0].getAttribute('data-doc-nav'));
  }
}

export function initDocs() {
  const root = document.querySelector('[data-docs]');
  if (!root) return;

  buildNav(root);
  renderSections(root);
  bindNav(root);
}
